import { useTranslations } from 'next-intl';

import { Position } from '@flipster/r230522/original/position';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { Button } from '@flipster/react/design-system/button';
import { numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { ProfitBoostEvent } from '@flipster/utils/segment/events/profit-boost';

import { MovableModalLegacy } from '../../movable-modal';
import { AboutProfitBoostContent } from './about-profit-boost-content';
import { useProfitBoost } from './use-profit-boost';

interface Props {
  symbol: string;
  positionId: Position['positionId'];
  location: 'assets_tab' | 'order_form_page'; // only for logging
  onNext: () => void;
}

export const AboutProfitBoostStep = ({ symbol, positionId, location, onNext }: Props) => {
  const transPositionProfitBoost = useTranslations('Position_profit_boost.r250401');
  const transTrade = useTranslations('Trade.r250401');
  const { profitBoost, position, discountRate, percent } = useProfitBoost(symbol, positionId);

  const formattedDuration = numberFormatter(profitBoost?.durationInMin);

  return (
    <>
      <MovableModalLegacy.Header css={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
        <MovableModalLegacy.Title
          label={transPositionProfitBoost('Profit_boost_title', {
            durationInMin: numberFormatter(profitBoost?.durationInMin),
          })}
          css={{
            textStyle: 'Title/L',
            textAlign: 'center',
            whiteSpace: 'pre-wrap',
          }}
        />
      </MovableModalLegacy.Header>
      <MovableModalLegacy.Content css={{ pt: { base: 8, tabletDown: 4 } }}>
        <AboutProfitBoostContent
          boostDailyLimitCount={position?.boostDailyLimitCount ?? 0}
          boostDailyRemainingCount={position?.boostDailyRemainingCount ?? 0}
          durationsInMin={formattedDuration}
        />
      </MovableModalLegacy.Content>
      <MovableModalLegacy.Footer>
        <Tooltip placement='top' open={discountRate > 0} shape='promotion'>
          <Tooltip.Trigger asChild>
            <Button
              label={transTrade('Continue_to_payment')}
              onClick={() => {
                segment.track(ProfitBoostEvent.ProfitBoostContinuePaymentClick, {
                  event_location: location,
                  position_id: positionId,
                  boost_cost: Number(profitBoost?.cost || 0),
                  discount_rate: discountRate,
                  duration: Number(formattedDuration),
                  remaining_boost: position?.boostDailyRemainingCount || 0,
                });
                onNext();
              }}
            />
          </Tooltip.Trigger>
          <Tooltip.Content
            css={{
              background: 'inverse.white',
              color: 'inverse.black',
              textStyle: 'Text/XXS/Strong',

              '& > div:first-child': {
                borderTopColor: 'inverse.white',
              },
            }}
          >
            {transPositionProfitBoost('Discount_tooltip', { percent: `${percent}%` })}
          </Tooltip.Content>
        </Tooltip>
      </MovableModalLegacy.Footer>
    </>
  );
};
